import { useRef, useState } from 'react';

// RDS
import Button from '@ramsey-design-system/button';
import { DownloadOutlinedIcon } from '@ramsey-design-system/icons';
import Link from '@ramsey-design-system/link';
import Text from '@ramsey-design-system/text';

// Components
import Dialog from '~fpu/shared/components/Dialog';
import ImageLoader from '~fpu/shared/components/ImageLoader';

// Utils
import { analyticsLogger } from '~fpu/shared/utils/analytics';
import cn from 'classnames';

interface DownloadableImageAsset {
  DESCRIPTION: string;
  ASSET_URLS: string[];
  THUMBNAIL_URL: string;
  CAPTION?: string;
  SIZES?: string[];
  PREVIEW_URLS?: string[];
  ELEMENT: string;
  LABEL: string;
}

/**
 * `tile` gives each image its own labelled call to action underneath — used
 * where the image sits in a sparse grid and the download needs to be obvious.
 *
 * `gallery` is the denser hover-to-zoom treatment used on Launch & Promote,
 * where the thumbnail itself is the only affordance.
 */
type DownloadableImageVariant = 'tile' | 'gallery';

interface DownloadableImageProps {
  image: DownloadableImageAsset;
  descriptionPrefix: string;
  variant?: DownloadableImageVariant;
  roundedBorder?: boolean;
  testId?: string;
}

function DownloadableImage({
  image,
  descriptionPrefix,
  variant = 'tile',
  roundedBorder = true,
  testId
}: DownloadableImageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isGallery = variant === 'gallery';
  const hasMultipleAssets = image.ASSET_URLS.length > 1;
  const altText = `${descriptionPrefix} - ${image.DESCRIPTION}`;

  const trackClick = () =>
    analyticsLogger.trackElementClicked({
      element: image.ELEMENT,
      label: image.LABEL
    });

  const openDialog = () => {
    setIsOpen(true);
    trackClick();
  };

  const thumbnail = isGallery ? (
    <img
      className={`duration-400 ${cn({
        'rounded-lg': roundedBorder
      })} max-h-48 transition ease-in-out hover:scale-105 hover:shadow-lg`}
      alt={altText}
      src={image.THUMBNAIL_URL}
    />
  ) : (
    <ImageLoader
      className={`mx-auto w-full overflow-clip ${cn({
        'rounded-lg': roundedBorder
      })}`}
      alt={altText}
      src={image.THUMBNAIL_URL}
    />
  );

  const assetDownloads = (
    <div
      className={
        isGallery
          ? 'gap-md mt-3 grid grid-cols-2 sm:mt-0 sm:grid-cols-4 sm:text-left'
          : 'sm:gap-lg bg-white pt-2 pb-2 sm:grid sm:grid-cols-2'
      }
    >
      {image.ASSET_URLS.map((url, index) => (
        <div
          key={url}
          className={
            isGallery
              ? 'flex w-full flex-col items-center text-center'
              : 'text-center'
          }
        >
          {isGallery ? (
            <img
              className="w-20 rounded-xl"
              alt={altText}
              src={image.PREVIEW_URLS ? image.PREVIEW_URLS[index] : url}
            />
          ) : (
            <a download href={url} onClick={trackClick}>
              <ImageLoader
                className="mx-auto w-full overflow-clip rounded-lg"
                alt={altText}
                src={image.PREVIEW_URLS ? image.PREVIEW_URLS[index] : url}
              />
            </a>
          )}

          {image.SIZES?.[index] && (
            <Text className="italic">{image.SIZES[index]}</Text>
          )}

          <Button
            appearance="subtle"
            className={isGallery ? 'p-1' : undefined}
            element="a"
            href={url}
            target="_blank"
            icon={DownloadOutlinedIcon}
            data-testid="download-fpu-media-image"
            onClick={trackClick}
          >
            Download
          </Button>
        </div>
      ))}
    </div>
  );

  return (
    <div className={isGallery ? undefined : 'text-center'}>
      {hasMultipleAssets ? (
        <>
          {isGallery ? (
            <Link
              element="button"
              ref={triggerRef}
              onClick={openDialog}
              data-testid={testId}
            >
              {thumbnail}
            </Link>
          ) : (
            <button ref={triggerRef} onClick={openDialog} data-testid={testId}>
              {thumbnail}
            </button>
          )}

          <Dialog
            open={isOpen}
            onClose={() => setIsOpen(false)}
            triggerRef={triggerRef}
            className={
              isGallery ? undefined : 'fixed inset-0 z-10 overflow-auto'
            }
          >
            <Dialog.Title hidden level="2">
              {altText}
            </Dialog.Title>
            {assetDownloads}
          </Dialog>
        </>
      ) : (
        <a
          download
          href={image.ASSET_URLS[0]}
          data-testid={testId}
          onClick={trackClick}
        >
          {thumbnail}
        </a>
      )}

      {image.CAPTION && <p className="italic">{image.CAPTION}</p>}

      {!isGallery &&
        (hasMultipleAssets ? (
          <Button appearance="subtle" onClick={openDialog}>
            Select Image
          </Button>
        ) : (
          <Button
            appearance="subtle"
            element="a"
            href={image.ASSET_URLS[0]}
            icon={DownloadOutlinedIcon}
            onClick={trackClick}
          >
            Download
          </Button>
        ))}
    </div>
  );
}

export type { DownloadableImageAsset, DownloadableImageVariant };
export default DownloadableImage;
